import React, { useMemo, useState } from "react";
import moment from "moment";

import { EventModal } from "./EventModal";
import { EventsHeader } from "./EventsHeader";
import { EventsContent } from "./EventsContent";
import { companySelector } from "../../../reducers/sessionReducer";
import { AppLayout } from "../../../components/app-layout/AppLayout";
import { EVENTS_STATUSES } from "../../../constants/EventConstants";
import { useShallowEqualSelector } from "../../../hooks/useShallowEqualSelector";
import { getCompanyStartOfDay, getCompanyEndOfDay } from "../../../helpers/DateUtils";
import { useEventsCalendarQuery } from "../../../queries/events/useEventsCalendarQuery";

export const EventsPage: React.FC = () => {
  // @ts-ignore
  const company = useShallowEqualSelector(companySelector);
  const companyId = company?.id || 1;

  const [month, setMonth] = useState(moment().month());
  const [year, setYear] = useState(moment().year());
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [selectedEvent, setSelectedEvent] = useState<PublicEvent | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState<string | null>(null);

  const monthsOptions = moment.months();

  const yearsOptions = useMemo(
    () => Array.from({ length: 10 }, (_, i) => moment().year() - 5 + i),
    [],
  );

  const calendarRange = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    return {
      start: getCompanyStartOfDay(firstDayOfMonth),
      end: getCompanyEndOfDay(lastDayOfMonth),
    };
  }, [year, month]);

  const { data: eventsData, isLoading } = useEventsCalendarQuery({
    companyId,
    startDate: calendarRange.start,
    endDate: calendarRange.end,
    statusCode: status,
    limit: 50,
  });

  const events: PublicEvent[] = useMemo(() => eventsData?.data?.list || eventsData?.list || [], [
    eventsData,
  ]);

  const calendarDays: DayCell[] = useMemo(() => {
    const days: DayCell[] = [];
    const firstDayOfMonth = new Date(year, month, 1);
    const currentDate = new Date(firstDayOfMonth);
    currentDate.setDate(currentDate.getDate() - currentDate.getDay());

    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month && date.getFullYear() === year;
      const dayStart = moment(date).startOf("day").valueOf();
      const dayEnd = moment(date).endOf("day").valueOf();
      const dayEvents = events.filter((e) => e.eventDate >= dayStart && e.eventDate <= dayEnd);
      days.push({ date, isCurrentMonth, events: dayEvents });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  }, [year, month, events]);

  const handleDateChange = (newYear: number, newMonth: number) => {
    setYear(newYear);
    setMonth(newMonth);
  };

  const handleArrowDateChange = (action: "next" | "previous") => {
    const minYear = moment().year() - 5;
    const maxYear = minYear + 10;
    if (action === "next") {
      if (month < 11) return handleDateChange(year, month + 1);
      if (year + 1 < maxYear) return handleDateChange(year + 1, 0);
    }
    if (action === "previous") {
      if (month > 0) return handleDateChange(year, month - 1);
      if (year - 1 >= minYear) return handleDateChange(year - 1, 11);
    }
  };

  const handleEventClick = (event: PublicEvent) => {
    setSelectedEvent(event);
    setRegistrationSuccess(null);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setRegistrationSuccess(null);
  };

  const handleStatusFilter = (statusCode: string | undefined) => {
    setStatus(statusCode);
  };

  const handleRegistrationSuccess = (message: string) => {
    setRegistrationSuccess(message);
  };

  const handleToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();

  return (
    <AppLayout title="Events Calendar" scrollable={true}>
      <div className="events-page">
        {/* Header: navigation + today button */}
        <EventsHeader
          year={year}
          month={month}
          isLoading={isLoading}
          handleToday={handleToday}
          yearsOptions={yearsOptions}
          monthsOptions={monthsOptions}
          handleDateChange={handleDateChange}
          handleArrowDateChange={handleArrowDateChange}
        />
        {/* Status filter bar */}
        <div className="events-status-filter">
          {EVENTS_STATUSES.map((s) => (
            <button
              key={s.label}
              type="button"
              className={`events-status-button ${
                status === s.value || (!status && !s.value) ? "active" : ""
              }`}
              onClick={() => handleStatusFilter(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Monthly calendar grid */}
        <EventsContent
          isToday={isToday}
          calendarDays={calendarDays}
          handleEventClick={handleEventClick}
        />

        {/* Event Modal (Details + Registration) */}
        {selectedEvent && (
          <EventModal
            event={selectedEvent}
            companyId={companyId}
            onClose={handleCloseModal}
            registrationSuccess={registrationSuccess}
            onRegistrationSuccess={handleRegistrationSuccess}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default EventsPage;
